import { matchPath } from 'react-router-dom';
import { SCOPE, type PermissionRequirement } from './permissions';

/**
 * Route → permission requirement. Module and sub-module names come straight
 * from GET /Modules/modules/with-submodules; keep them in sync with that
 * response rather than inventing new ones.
 *
 * Paths are react-router patterns and are matched most-specific-first, so
 * `/campaigns/new` wins over `/campaigns/:rest`. Anything not listed here is
 * unrestricted — the dashboard and profile deliberately stay open to everyone
 * who can sign in.
 */
export const ROUTE_PERMISSIONS: { path: string; requirement: PermissionRequirement }[] = [
  // ---- RBAC (Backoffice module) ----
  { path: '/rbac/roles/new', requirement: { module: 'Backoffice', subModule: 'RolePermission', scope: SCOPE.create } },
  { path: '/rbac/roles/edit/:id', requirement: { module: 'Backoffice', subModule: 'RolePermission', scope: SCOPE.update } },
  { path: '/rbac/roles', requirement: { module: 'Backoffice', subModule: 'RolePermission', scope: SCOPE.view } },
  { path: '/rbac/system-users', requirement: { module: 'Backoffice', subModule: 'Users', scope: SCOPE.view } },
  { path: '/rbac/audit-logs', requirement: { module: 'Backoffice', subModule: 'Users', scope: SCOPE.view } },

  // ---- Mehir app users. The permission matrix has no sub-module for them;
  // `Auth` is the module that owns the endpoints, so require module-level
  // access only. ----
  { path: '/users/details/:id', requirement: { module: 'Auth' } },
  { path: '/users/list', requirement: { module: 'Auth' } },
  { path: '/users', requirement: { module: 'Auth' } },

  // ---- Contracts ----
  { path: '/contracts/details/:id', requirement: { module: 'Contract', subModule: 'Contract', scope: SCOPE.view } },
  { path: '/contracts/savings-plan/:id', requirement: { module: 'Contract', subModule: 'Contract', scope: SCOPE.view } },
  { path: '/contracts/savings-plans', requirement: { module: 'Contract', subModule: 'Contract', scope: SCOPE.view } },
  { path: '/contracts/list', requirement: { module: 'Contract', subModule: 'Contract', scope: SCOPE.view } },
  { path: '/contracts', requirement: { module: 'Contract', subModule: 'Contract', scope: SCOPE.view } },

  // ---- Campaigns ----
  { path: '/campaigns/new', requirement: { module: 'Campaign', subModule: 'Campaign', scope: SCOPE.create } },
  { path: '/campaigns/edit/:id', requirement: { module: 'Campaign', subModule: 'Campaign', scope: SCOPE.update } },
  { path: '/campaigns/codes/:id', requirement: { module: 'Campaign', subModule: 'Code', scope: SCOPE.view } },
  { path: '/campaigns/details/:id', requirement: { module: 'Campaign', subModule: 'Campaign', scope: SCOPE.view } },
  { path: '/campaigns/list', requirement: { module: 'Campaign', subModule: 'Campaign', scope: SCOPE.view } },
  { path: '/campaigns', requirement: { module: 'Campaign', subModule: 'Campaign', scope: SCOPE.view } },

  // ---- Referrals: served by the auth service, same reasoning as users ----
  { path: '/referrals/earnings/:userId', requirement: { module: 'Auth' } },
  { path: '/referrals/earnings', requirement: { module: 'Auth' } },
  { path: '/referrals/manual-payment', requirement: { module: 'Auth' } },
  { path: '/referrals/details/:id', requirement: { module: 'Auth' } },
  { path: '/referrals/list', requirement: { module: 'Auth' } },

  // ---- Content ----
  { path: '/content/blog/new', requirement: { module: 'Blog', subModule: 'Blog', scope: SCOPE.create } },
  { path: '/content/blog/edit/:id', requirement: { module: 'Blog', subModule: 'Blog', scope: SCOPE.update } },
  { path: '/content/blog/featured', requirement: { module: 'Blog', subModule: 'Blog', scope: SCOPE.update } },
  { path: '/content/blog/list', requirement: { module: 'Blog', subModule: 'Blog', scope: SCOPE.view } },
  { path: '/content/news/new', requirement: { module: 'Blog', subModule: 'Announcement', scope: SCOPE.create } },
  { path: '/content/news/edit/:id', requirement: { module: 'Blog', subModule: 'Announcement', scope: SCOPE.update } },
  { path: '/content/news/list', requirement: { module: 'Blog', subModule: 'Announcement', scope: SCOPE.view } },

  // ---- Fit: the matrix exposes a single Exercise sub-module for the module ----
  { path: '/fit/videos/new', requirement: { module: 'Fit', subModule: 'Exercise', scope: SCOPE.create } },
  { path: '/fit/videos/edit/:id', requirement: { module: 'Fit', subModule: 'Exercise', scope: SCOPE.update } },
  { path: '/fit/videos', requirement: { module: 'Fit', subModule: 'Exercise', scope: SCOPE.view } },
  { path: '/fit/exercises/new', requirement: { module: 'Fit', subModule: 'Exercise', scope: SCOPE.create } },
  { path: '/fit/exercises/edit/:id', requirement: { module: 'Fit', subModule: 'Exercise', scope: SCOPE.update } },
  { path: '/fit/exercises', requirement: { module: 'Fit', subModule: 'Exercise', scope: SCOPE.view } },
  { path: '/fit/user-exercises/details/:id', requirement: { module: 'Fit', subModule: 'Exercise', scope: SCOPE.view } },
  { path: '/fit/user-exercises', requirement: { module: 'Fit', subModule: 'Exercise', scope: SCOPE.view } },
  { path: '/fit/targets/new', requirement: { module: 'Fit', subModule: 'Exercise', scope: SCOPE.create } },
  { path: '/fit/targets/edit/:id', requirement: { module: 'Fit', subModule: 'Exercise', scope: SCOPE.update } },
  { path: '/fit/targets', requirement: { module: 'Fit', subModule: 'Exercise', scope: SCOPE.view } },
  { path: '/fit/levels/new', requirement: { module: 'Fit', subModule: 'Exercise', scope: SCOPE.create } },
  { path: '/fit/levels/edit/:id', requirement: { module: 'Fit', subModule: 'Exercise', scope: SCOPE.update } },
  { path: '/fit/levels', requirement: { module: 'Fit', subModule: 'Exercise', scope: SCOPE.view } },
  { path: '/fit/exercise-types/new', requirement: { module: 'Fit', subModule: 'Exercise', scope: SCOPE.create } },
  { path: '/fit/exercise-types/edit/:id', requirement: { module: 'Fit', subModule: 'Exercise', scope: SCOPE.update } },
  { path: '/fit/exercise-types', requirement: { module: 'Fit', subModule: 'Exercise', scope: SCOPE.view } },

  // ---- Finance ----
  { path: '/finance/statistics', requirement: { module: 'Finance' } },
  { path: '/finance/news-management', requirement: { module: 'Finance' } },

  // ---- Settings: these live under the Blog module in the matrix ----
  { path: '/settings/faq-categories', requirement: { module: 'Blog', subModule: 'Faq', scope: SCOPE.view } },
  { path: '/settings/faq-management', requirement: { module: 'Blog', subModule: 'Faq', scope: SCOPE.view } },
  { path: '/settings/app-contracts', requirement: { module: 'Blog', subModule: 'AppContracts', scope: SCOPE.view } },
  { path: '/settings/contact-info', requirement: { module: 'Blog', subModule: 'ContactInfo', scope: SCOPE.view } },
  { path: '/settings/communication-permissions', requirement: { module: 'Auth' } },
];

/** Sorted longest-first so `/campaigns/new` is tested before `/campaigns`. */
export const ROUTE_PERMISSIONS_BY_SPECIFICITY = [...ROUTE_PERMISSIONS].sort(
  (a, b) => b.path.split('/').length - a.path.split('/').length
);

/** Requirement guarding a pathname, or null when the route is unrestricted. */
export const requirementForPath = (pathname: string): PermissionRequirement | null =>
  ROUTE_PERMISSIONS_BY_SPECIFICITY.find((entry) => matchPath(entry.path, pathname))?.requirement ??
  null;
